// ---------------------------------------------------------------------------
// Theme configuration: convert API/CMS theme data into CSS variables and load
// custom fonts. Types and defaults come from the ORM package.
// ---------------------------------------------------------------------------

import {
  DEFAULT_THEME,
  type CustomFontDef,
  type ThemeState,
} from "@superbright/indexeddb-orm";

import { normalizeFontUrl } from "./normalizeFontUrl";
import { computeThemeTokens } from "./themeTokens";
import type { EmbedTheme, EmbedThemeFont } from "./types";

const DEFAULT_FONT_STACK = "FTRegularNeue, sans-serif";

const FONT_FORMAT: Record<string, string> = {
  WOFF2: "woff2",
  WOFF: "woff",
  TTF: "truetype",
  OTF: "opentype",
};

// ---------------------------------------------------------------------------
// Embed API → ThemeState
// ---------------------------------------------------------------------------

const fontValueOf = (font: EmbedThemeFont) =>
  `user__${font.id}__${font.family}`;

function toCustomFont(font: EmbedThemeFont): CustomFontDef {
  const format = FONT_FORMAT[font.format] ?? "woff2";
  return {
    value: fontValueOf(font),
    family: font.family,
    src: `url("${normalizeFontUrl(
      font.url,
      window.location.protocol
    )}") format("${format}")`,
    stack: `"${font.family}", sans-serif`,
    weight: String(font.weight || 400),
    style: font.style || "normal",
  };
}

/**
 * Convert the embed API's theme payload into a complete ThemeState. Missing
 * config fields fall back to DEFAULT_THEME, and any custom fonts assigned to
 * heading/body roles override the corresponding font slot.
 */
export function resolveEmbedThemeFonts(embedTheme: EmbedTheme): ThemeState {
  const config = { ...DEFAULT_THEME, ...embedTheme.config };
  const customFonts = embedTheme.fonts.map((ref) => toCustomFont(ref.font));

  const fontForRole = (role: "HEADING" | "BODY") =>
    embedTheme.fonts.find((f) => f.role === role)?.font;

  const heading = fontForRole("HEADING");
  const body = fontForRole("BODY");

  return {
    ...config,
    heading_font: heading ? fontValueOf(heading) : config.heading_font,
    body_font: body ? fontValueOf(body) : config.body_font,
    custom_fonts: customFonts.length > 0 ? customFonts : undefined,
  };
}

// ---------------------------------------------------------------------------
// ThemeState → CSS tokens
// ---------------------------------------------------------------------------

/** Look up the CSS font-family stack for a heading/body font slot. */
function fontStackFor(value: string, customFonts: CustomFontDef[]): string {
  if (value === "__default__") return DEFAULT_FONT_STACK;
  return (
    customFonts.find((f) => f.value === value)?.stack ?? DEFAULT_FONT_STACK
  );
}

/** Custom font definitions for the heading/body slots, deduped. */
function selectedCustomFonts(config: ThemeState): CustomFontDef[] {
  const customFonts = config.custom_fonts ?? [];
  const result: CustomFontDef[] = [];
  const seen = new Set<string>();

  for (const value of [config.heading_font, config.body_font]) {
    if (value === "__default__" || seen.has(value)) continue;
    const font = customFonts.find((f) => f.value === value);
    if (!font) continue;
    seen.add(value);
    result.push(font);
  }
  return result;
}

/**
 * Map a ThemeState to a flat record of CSS custom properties. Consumers apply
 * these via `element.style.setProperty` — no CSS string parsing needed.
 */
export function getThemeTokens(config: ThemeState): Record<string, string> {
  const customFonts = config.custom_fonts ?? [];
  return computeThemeTokens(
    config,
    fontStackFor(config.heading_font, customFonts),
    fontStackFor(config.body_font, customFonts)
  );
}

/** Apply theme tokens to `:root` via inline styles (highest specificity). */
export function applyThemeTokens(tokens: Record<string, string>): void {
  const root = document.documentElement;
  for (const [prop, value] of Object.entries(tokens)) {
    root.style.setProperty(prop, value);
  }
}

// ---------------------------------------------------------------------------
// Custom font loading via the FontFace API
// ---------------------------------------------------------------------------

const activeFontFaces: FontFace[] = [];

/**
 * Load the heading/body custom fonts via the FontFace API. Previously loaded
 * fonts are cleared first so theme changes don't leak across sessions.
 */
export async function registerThemeFonts(config: ThemeState): Promise<void> {
  for (const face of activeFontFaces) document.fonts.delete(face);
  activeFontFaces.length = 0;

  const fonts = selectedCustomFonts(config);
  if (fonts.length === 0) return;

  await Promise.allSettled(
    fonts.map(async (font) => {
      const face = new FontFace(font.family, font.src, {
        weight: font.weight,
        style: font.style,
        display: "block",
      });
      await face.load();
      document.fonts.add(face);
      activeFontFaces.push(face);
    })
  );
}

/**
 * True when the resolved theme assigns a custom font to the heading or body
 * slot — i.e. there are font files we must download before revealing the page.
 * False for default-font themes (nothing to wait for, no font swap to hide).
 */
export function hasCustomThemeFonts(config: ThemeState): boolean {
  return selectedCustomFonts(config).length > 0;
}

/**
 * Resolve once the theme's selected heading/body custom fonts have finished
 * loading (or failed). Use when the `@font-face` rules are already present in
 * the document (SSR-injected): it triggers the load via the Font Loading API
 * and awaits completion. No-op when there are no custom fonts or no `document`.
 */
export async function awaitThemeFontsReady(config: ThemeState): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  const fonts = selectedCustomFonts(config);
  if (fonts.length === 0) return;
  await Promise.allSettled(
    fonts.map((font) =>
      document.fonts.load(`${font.style} ${font.weight} 1em "${font.family}"`)
    )
  );
}
